import TaskService from '@/services/task.service'
import SiteService from '@/services/site.service'
import EventService from '@/services/event.service'
import UserService from '@/services/user.service'
import {
  UserTaskType,
  foundUsersDialog,
  notifyUsersDialog,
  setPaymentDialog,
  closeTask
} from '@/store/modules/task'
import notifyDialog from '@/components/NotifyDialog.vue'
import cancelTaskDialog from '@/components/CancelTaskDialog.vue'
import closeDialog from '@/components/CloseTaskDialog.vue'
import paidCard from '@/components/PaymentDialog.vue'
import sendPatrolCard from '@/components/patrol/SendPatrolDialog.vue'
import DownloadEventsDialog from '@/components/events/dialogs/DownloadEventsDialog.vue'
import AssignToOperatorDialog from '@/components/events/dialogs/AssignToOperatorDialog.vue'
import { format } from 'date-fns'

/**
 * Factory for creating event action methods
 * @param {Object} vm - Vue component instance (this)
 * @returns {Object} Action methods bound to component instance
 */
export function createEventActions(vm) {

  const markLocalEventChange = () => {
    if (vm?.event?.id) {
      vm.$store.dispatch('dashboard/suppressLocalEventSignal', { eventId: vm.event.id })
    }
  }

  const startDispatcherTask = () => {
    if (!vm.event?.site?.id) {
      console.error('Cannot start dispatcher task: missing event.site.id')
      vm.$dialog.notify.error('Error: Missing site data')
      return
    }

    if (vm.dispatcherTask) {
      TaskService.restartTask({
        siteId: vm.event.site.id,
        taskId: vm.dispatcherTask.id,
        message: vm.dispatcherTask.details.status.message,
        comment: vm.dispatcherTask.details.status.comment
      }).then(res => {
        markLocalEventChange()
        vm.addTask(res.data)
      })
    } else {
      TaskService.create(vm.event.id, {
        assignees: [],
        task_type: UserTaskType.DISPATCHER,
        description: ""
      }).then(res => {
        markLocalEventChange()
        vm.addTask(res.data)
      })
    }
  }

  const closeAllTasks = async () => {
    // Validate required data before calling closeTask
    if (!vm.event?.site?.id) {
      console.error('Cannot close tasks: missing event.site.id', {
        event: vm.event,
        hasSite: !!vm.event?.site,
        siteId: vm.event?.site?.id,
        eventId: vm.event?.id
      })
      vm.$dialog.notify.error('Error: Missing site data. Please refresh the page and try again.')
      return
    }

    if (!vm.dispatcherTask) {
      console.error('Cannot close tasks: missing dispatcherTask')
      vm.$dialog.notify.error('Error: No task to close')
      return
    }

    await closeTask({
      context: vm,
      dialogComponent: closeDialog,
      event: vm.event,
      task: vm.dispatcherTask,
      taskService: TaskService,
      siteService: SiteService
    }).then(() => {
      markLocalEventChange()
      vm.refresh()
    }).catch(error => {
      console.error('Error in closeAllTasks:', error)
      vm.$dialog.notify.error('Error closing tasks')
    })
  }

  const cancelTask = async () => {
    if (!vm.event?.site?.id) {
      console.error('Cannot cancel task: missing event.site.id')
      vm.$dialog.notify.error('Error: Missing site data')
      return
    }

    const dialogInstance = await vm.$dialog.show(cancelTaskDialog, {
      message: vm.dispatcherTask.details.status.message,
      comment: vm.dispatcherTask.details.status.comment,
      cancelPassword: vm.event?.site?.password,
      users: new Promise(resolve => {
        SiteService.fetchMembers(vm.event.site.id).then(result =>
          resolve(result.map(member => member.user))
        )
      })
    })
    dialogInstance.waitForResult = true
    dialogInstance.wait().then(result => {
      if (!result) return

      const task = vm.dispatcherTask
      TaskService.cancelTask(
        vm.event.site.id,
        task.id,
        result.message,
        result.comment,
        result.user
      ).then(res => {
        markLocalEventChange()
        vm.addTask(res.data)
      })
    })
  }

  const startPatrolTask = async () => {
    const dialogInstance = await vm.$dialog.show(sendPatrolCard)
    dialogInstance.waitForResult = true
    dialogInstance.wait().then(result => {
      if (!result) return

      let patrol = result.selected
      TaskService.create(vm.event.id, {
        assignees: [{ user_id: patrol.id }],
        task_type: UserTaskType.PATROL,
        description: result.description,
        details: {
          options: { can_closed: result.canClose }
        },
        parent_id: vm.dispatcherTask.id
      }).then(() => {
        markLocalEventChange()
        vm.refresh()
      })
    })
  }

  const notifyUsers = async () => {
    const args = {
      context: vm,
      notifyDialog: notifyDialog,
      taskService: TaskService,
      siteService: SiteService,
      event: vm.event,
      task: vm.dispatcherTask,
    }
    await notifyUsersDialog(args).then(() => {
      markLocalEventChange()
      vm.refresh()
    })
  }

  const foundUsers = async () => {
    const args = {
      context: vm,
      foundDialog: notifyDialog,
      taskService: TaskService,
      siteService: SiteService,
      site: vm.event.site,
      task: vm.dispatcherTask,
    }
    await foundUsersDialog(args).then(() => {
      markLocalEventChange()
      vm.refresh()
    })
  }

  const setPayment = async () => {
    await setPaymentDialog(vm, paidCard, TaskService, vm.allTasks, vm.event.site).then(res => {
      markLocalEventChange()
      res.forEach(element => {
        vm.addTask(element)
      })
    })
  }

  const assignToMe = async () => {
    const task = vm.dispatcherTask
    TaskService.assignOnlyToMe(vm.event.site.id, task.id).then(() => {
      markLocalEventChange()
      vm.refreshEvent()
      vm.$store.dispatch('dashboard/fetchOpenEvents')
    })
  }

  const releaseFromMe = async () => {
    const task = vm.dispatcherTask
    TaskService.releaseFromMe(vm.event.site.id, task.id).then(() => {
      markLocalEventChange()
      vm.refreshEvent()
      vm.$store.dispatch('dashboard/fetchOpenEvents')
    })
  }

  const assignToOther = async () => {
    const task = vm.dispatcherTask
    const isServiceTask = task?.task_type === UserTaskType.SERVICE
    const fetchCandidates = task?.task_type === UserTaskType.SERVICE
      ? UserService.fetchServicers.bind(UserService)
      : UserService.fetchDispatchers.bind(UserService)

    const dialogInstance = await vm.$dialog.show(AssignToOperatorDialog, {
      comboLabel: isServiceTask ? 'Serwisant' : 'Dyżurny',
      subtitle: isServiceTask
        ? 'Wybierz serwisanta, któremu chcesz przekazać obsługę tego zdarzenia'
        : 'Wybierz dyżurnego, któremu chcesz przekazać obsługę tego zdarzenia',
      users: new Promise(resolve => {
        fetchCandidates("").then(data => {
          // Exclude current user
          const users = data.results.filter(user => user.id !== vm.user.id)
          resolve(users)
        }).catch(error => {
          console.error('Error fetching transfer candidates:', error)
          resolve([])
        })
      })
    })

    dialogInstance.waitForResult = true
    dialogInstance.wait().then(result => {
      if (result === false || result === undefined) return

      TaskService.forwardTo(vm.event.site.id, task.id, result.id).then(() => {
        markLocalEventChange()
        vm.refreshEvent()
        vm.$store.dispatch('dashboard/fetchOpenEvents')
      })
    })
  }

  const onEditEvent = () => {
    vm.$router.push({
      name: "Edit Event",
      params: {
        eventId: vm.event.id,
        event: vm.event,
        expert: true
      }
    })
  }

  const onDeleteEvent = async () => {
    const confirmCode = `${vm.event.site.code_name} ${vm.event.site.name}`
    const result = await vm.$dialog.prompt({
      text: `Dla potwierdzenia wpisz: ${confirmCode}`,
      value: "",
      title: "Czy na pewno chcesz usunąć zdarzenie?",
      rules: [],
      actions: {
        false: "Anuluj",
        true: {
          color: "critical",
          text: "OK"
        }
      }
    })

    if (result === false || result === undefined || result != confirmCode) {
      return
    }

    EventService.delete(vm.event).then(res => {
      if (res.status == 204) {
        vm.$router.push({ name: 'Events' })
      }
    })
  }

  const onDownload = async () => {
    const result = await vm.$dialog.show(DownloadEventsDialog, {
      title: "Zdarzenie",
      waitForResult: true,
    })

    if (result === false || result === undefined) {
      return
    }

    var query = {
      id: `${vm.event.id}`,
      ...result,
    }

    EventService.download(vm.event.site.id, query).then(response => {
      const blob = new Blob([response.data], {
        type: "application/pdf;charset=utf-8;"
      })
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `raport_${format(new Date(), "yyyMMddHHmmss")}.pdf`
      link.click()
      URL.revokeObjectURL(link.href)
    })
  }

  return {
    startDispatcherTask,
    closeAllTasks,
    cancelTask,
    startPatrolTask,
    notifyUsers,
    foundUsers,
    setPayment,
    assignToMe,
    releaseFromMe,
    assignToOther,
    onEditEvent,
    onDeleteEvent,
    onDownload
  }
}
